import { Alert, Button, Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { CircleExclamationSolid, XMarkSolid } from '../../../utils/icons';
import { useState } from 'react';
import { passwordRegex } from '../../../utils';
import Success from '../../Success';
import useApi from '../../../utils/api';
import { recoverPassword } from '../../../utils/actions';
import { notificationMessage } from '../../../utils/hooks';

const RecoveryPasswordForm = ({ email, goToLogin }: { email: string; goToLogin: () => void }) => {
  const [hasFormErrors, setHasFormErrors] = useState<boolean>(true);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { api } = useApi();

  const onFinish = () => {
    const { code, password } = form.getFieldsValue();
    setIsLoading(true);
    api(recoverPassword({ code, email, password }))
      .then((resp) => {
        if (resp.status === 200) {
          const { message } = resp.data;

          if (message) {
            notificationMessage({
              message,
              type: 'error',
            });
          } else {
            setShowSuccess(true);
          }
        }
      })
      .finally(() => setIsLoading(false));
  };

  const checkValidForm = () => {
    const { code, password, password2 } = form.getFieldsValue();
    const hasInputWithErrors =
      !code ||
      !password ||
      !password2 ||
      form
        .getFieldsError()
        .map((e) => e.errors)
        .flat().length !== 0;

    setHasFormErrors(hasInputWithErrors);
  };

  return (
    <div className="h-full md:px-[52px]">
      {!showSuccess && (
        <>
          <h1 className="mb-[32px] mt-[72px] hidden text-center md:block">
            {t('login.resetPassword')}
          </h1>
          <h3 className="mb-[32px] mt-[24px] text-center md:hidden">{t('login.resetPassword')}</h3>
          <Alert
            message={
              <span>
                {t('login.weHaveSentCode')} <u>{email}</u>
              </span>
            }
            type="info"
            icon={<CircleExclamationSolid />}
            closable
            showIcon
            closeIcon={<XMarkSolid />}
            className="mb-[24px]"
          />
          <Form form={form} onFinish={onFinish} onFieldsChange={checkValidForm}>
            <Form.Item
              label={t('login.code.label')}
              name="code"
              rules={[
                {
                  required: true,
                  message: String(t('login.code.required')),
                },
                {
                  min: 6,
                  message: String(t('login.code.error')),
                },
              ]}
            >
              <Input placeholder={String(t('login.code.placeholder'))} maxLength={6} />
            </Form.Item>
            <Form.Item
              required
              name="password"
              label={t('login.password')}
              rules={[
                {
                  required: true,
                  message: String(t('common.password.required')),
                },
                {
                  pattern: passwordRegex,
                  message: String(t('common.password.error')),
                },
              ]}
            >
              <Input.Password maxLength={20} placeholder={t('common.password.placeHolder')} />
            </Form.Item>
            <Form.Item
              required
              name="password2"
              label={String(t('login.repeatPassword'))}
              rules={[
                {
                  required: true,
                  message: String(t('common.repeatPassword.required')),
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(String(t('common.repeatPassword.error'))));
                  },
                }),
              ]}
            >
              <Input.Password maxLength={20} placeholder={t('common.repeatPassword.placeHolder')} />
            </Form.Item>
            <Button
              className={`primary mt-[12px] w-full ${hasFormErrors ? 'primaryDisabled' : ''}`}
              htmlType="submit"
              loading={isLoading}
            >
              {t('common.next')}
            </Button>
            <div className="my-[24px] flex justify-center">
              <Button onClick={goToLogin} htmlType="button" className="ghost">
                <p className="link">{t('common.back')}</p>
              </Button>
            </div>
          </Form>
        </>
      )}
      {showSuccess && (
        <Success
          buttonText={t('login.successRecoveryPassword.buttonText')}
          onClick={goToLogin}
          text={t('login.successRecoveryPassword.text')}
          title={t('login.successRecoveryPassword.title')}
        />
      )}
    </div>
  );
};

export default RecoveryPasswordForm;
